import React, { useState, useEffect } from "react";
import axios from "axios";
import logosyn from "../assets/logosym.png";
import LogoutConfirmModal from "../components/LogoutConfirmModal";

const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

// ── Helpers ───────────────────────────────────────────────────────────────────

function performLogout() {
  localStorage.removeItem("token");
  localStorage.removeItem("jwt");
  localStorage.removeItem("role");
  localStorage.removeItem("email");
  localStorage.removeItem("name");
  window.location.href = "/login";
}

/** "Ajin Mathew" → "AM",  "SuperUser HumanResource" → "SH" */
function getInitials(name = "") {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w.charAt(0).toUpperCase())
    .join("");
}

const AVATAR_COLORS = [
  "bg-violet-600", "bg-blue-600", "bg-emerald-600", "bg-amber-500",
  "bg-rose-600",   "bg-cyan-600", "bg-fuchsia-600", "bg-teal-600",
];
function avatarColor(name = "") {
  const sum = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

function capitalise(str = "") {
  return str.replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Try to resolve the display name without any network call:
 *
 *  1. localStorage "name"   — set at login (fastest)
 *  2. JWT payload fields    — present in tokens issued after the auth update
 *
 * Returns "" when neither is available (old token, no cache) — the
 * useEffect will then call /users/me to fetch it.
 */
function resolveNameSync() {
  // 1. Cached from previous fetch or login
  const cached = (localStorage.getItem("name") || "").trim();
  if (cached) return cached;

  // 2. JWT payload (new tokens only)
  try {
    const token = localStorage.getItem("jwt") || localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    const name = [payload.firstName, payload.lastName]
      .filter(Boolean).join(" ").trim();
    if (name) {
      localStorage.setItem("name", name); // cache for next render
      return name;
    }
  } catch { /* ignore */ }

  return "";
}

// ── Component ─────────────────────────────────────────────────────────────────

const Navbar = ({ onLogout }) => {
  const token = localStorage.getItem("jwt") || localStorage.getItem("token") || "";
  const role  = localStorage.getItem("role") || "";

  const [displayName, setDisplayName] = useState(resolveNameSync);
  const [showConfirm, setShowConfirm] = useState(false);

  /**
   * Only hit /users/me when we couldn't resolve the name synchronously.
   * This means fresh logins (name already in localStorage) pay zero cost.
   * Old sessions (JWT without name fields) make exactly one call, cache the
   * result, and never call again until the user logs out and back in.
   */
  useEffect(() => {
    if (displayName || !token) return; // already resolved — nothing to do

    axios
      .get(`${API}/users/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const first = (res.data?.first_name || "").trim();
        const last  = (res.data?.last_name  || "").trim();
        const name  = [first, last].filter(Boolean).join(" ");
        if (name) {
          localStorage.setItem("name", name);
          setDisplayName(name);
        }
      })
      .catch(() => {
        /* 404 / network error — server may not have restarted yet.
           Leave displayName as "" so Navbar shows "…" gracefully.
           No email prefix, no broken display. */
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogoutConfirmed = () => {
    setShowConfirm(false);
    if (onLogout) onLogout();
    else performLogout();
  };

  const initials  = getInitials(displayName);
  const avatarBg  = avatarColor(displayName);
  const roleLabel = capitalise(role);

  return (
    <>
      <nav className="w-full py-3 px-8 flex items-center justify-between ui-card shadow-neon">

        {/* ── Brand ── */}
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg overflow-hidden flex items-center justify-center">
            <img src={logosyn} alt="Tasket" className="h-10 w-auto object-contain" />
          </div>
          <div>
            <div className="text-lg font-bold" style={{ fontFamily: "Inter, sans-serif" }}>
              Tasket
            </div>
            <div className="text-xs text-muted">Greenated's Task Management System</div>
          </div>
        </div>

        {/* ── User identity + Logout ── */}
        <div className="flex items-center gap-3">

          {/* Initials avatar */}
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center
              text-white font-bold text-sm select-none flex-shrink-0 ${avatarBg}`}
            title={displayName || "User"}
          >
            {initials || "?"}
          </div>

          {/* Name + Role */}
          <div className="hidden md:flex flex-col items-start leading-tight">
            <span className="text-sm font-semibold text-muted">
              {displayName || "…"}
            </span>
            {roleLabel && (
              <span className="text-xs text-muted opacity-70">{roleLabel}</span>
            )}
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px h-8 bg-white/10 mx-1" />

          {/* Logout */}
          <button
            onClick={() => setShowConfirm(true)}
            className="px-3 py-2 rounded-lg text-sm border border-transparent hover:border-white/10 ui-card transition"
          >
            Logout
          </button>
        </div>
      </nav>

      {showConfirm && (
        <LogoutConfirmModal
          mode="confirm"
          onConfirm={handleLogoutConfirmed}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </>
  );
};

export default Navbar;
